import { Link } from "react-router-dom";
import { Diamond, Sparkles, Award, ChevronRight, ArrowLeft } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="about-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[#C9A962]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="relative">
                <Diamond className="w-10 h-10 text-[#C9A962]" />
                <Sparkles className="w-4 h-4 text-[#C9A962] absolute -top-1 -right-1" />
              </div>
              <div>
                <span
                  className="text-xl font-semibold tracking-wide text-[#C9A962]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  VK Gems & Jewels
                </span>
                <p className="text-[10px] text-[#8B7355] uppercase tracking-[0.2em]">Since 1995</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors">Home</Link>
              <Link to="/shop" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors">Shop</Link>
              <Link to="/about" className="text-sm text-[#C9A962]">About</Link>
              <Link to="/#locations" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors">Locations</Link>
              <Link to="/#contact" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 border-b border-[#C9A962]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C9A962] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-[#C9A962] to-transparent" />
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A962]">Our Legacy</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="about-hero-title"
          >
            A Heritage of <span className="text-[#C9A962]">Exceptional Craft</span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0A] to-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#C9A962]/30" />
              <img
                src="https://images.unsplash.com/photo-1715374033196-0ff662284a7e?crop=entropy&cs=srgb&fm=jpg&w=800"
                alt="Artisan crafting jewelry"
                className="relative z-10 w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#C9A962] p-6 z-20">
                <p
                  className="text-4xl font-bold text-[#0A0A0A]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  30+
                </p>
                <p className="text-sm text-[#0A0A0A]/70">Years of Excellence</p>
              </div>
            </div>

            <div>
              <h2
                className="text-3xl sm:text-4xl font-light text-white mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Our <span className="text-[#C9A962]">Story</span>
              </h2>
              <p className="text-[#B8A88A] mb-6 leading-relaxed">
                Since 1995, VK Gems & Jewels has been at the forefront of India's jewelry
                manufacturing industry. Our master artisans combine traditional techniques with
                modern innovation to create pieces that transcend time.
              </p>
              <p className="text-[#B8A88A] mb-6 leading-relaxed">
                Every gemstone we source is ethically obtained and certified. Every piece of
                jewelry is crafted with precision and passion. We believe in creating not just
                ornaments, but heirlooms that carry stories across generations.
              </p>
              <p className="text-[#B8A88A] mb-8 leading-relaxed">
                From our manufacturing hub in Srikakulam to our corporate office in Gurgaon and
                our USA sales office in Lincoln, Nebraska, we serve clients across 50+ countries
                with the same dedication to craft, integrity, and beauty.
              </p>

              <div className="grid grid-cols-3 gap-6" data-testid="about-stats">
                <div className="text-center p-4 border border-[#C9A962]/20">
                  <p
                    className="text-3xl font-light text-[#C9A962]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    500+
                  </p>
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mt-1">Designs</p>
                </div>
                <div className="text-center p-4 border border-[#C9A962]/20">
                  <p
                    className="text-3xl font-light text-[#C9A962]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    50+
                  </p>
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mt-1">Countries</p>
                </div>
                <div className="text-center p-4 border border-[#C9A962]/20">
                  <p
                    className="text-3xl font-light text-[#C9A962]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    10K+
                  </p>
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mt-1">Happy Clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-light text-white mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              What We <span className="text-[#C9A962]">Stand For</span>
            </h2>
            <p className="text-[#B8A88A] max-w-2xl mx-auto">
              Three decades of relentless commitment to the people, the craft, and the stones.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-8 bg-[#0F0F0F] border border-[#C9A962]/20">
              <Award className="w-8 h-8 text-[#C9A962] mb-4" />
              <h3
                className="text-xl text-white mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Certified Quality
              </h3>
              <p className="text-sm text-[#8B7355] leading-relaxed">
                Every diamond and gemstone is IGI / GIA certified. No compromise on grade,
                provenance, or authenticity.
              </p>
            </div>
            <div className="p-8 bg-[#0F0F0F] border border-[#C9A962]/20">
              <Sparkles className="w-8 h-8 text-[#C9A962] mb-4" />
              <h3
                className="text-xl text-white mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Ethical Sourcing
              </h3>
              <p className="text-sm text-[#8B7355] leading-relaxed">
                Conflict-free stones and transparent supply chains. Lab-grown options for every
                client who values sustainability.
              </p>
            </div>
            <div className="p-8 bg-[#0F0F0F] border border-[#C9A962]/20">
              <Diamond className="w-8 h-8 text-[#C9A962] mb-4" />
              <h3
                className="text-xl text-white mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Master Artisanship
              </h3>
              <p className="text-sm text-[#8B7355] leading-relaxed">
                In-house master jewelers and CAD designers craft every piece by hand, finished to
                the highest tolerances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#7B2D26] via-[#8B3D36] to-[#7B2D26]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-3xl sm:text-4xl font-light text-white mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ready to Find Your Heirloom?
          </h2>
          <p className="text-[#F5D0CC] mb-8">
            Browse our full collection of certified jewelry — every piece tells a story.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#7B2D26] font-semibold hover:bg-[#F5F5F0] transition-all"
            data-testid="about-shop-cta"
          >
            Explore Collection
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="py-12 bg-[#0A0A0A] border-t border-[#C9A962]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm text-[#8B7355]">
            © 2025 VK Gems & Jewels. All rights reserved. IGI & GIA Certified Manufacturer.
          </p>
        </div>
      </footer>
    </div>
  );
}
